import clsx from 'clsx';
import Image from 'next/image';
import Head from 'next/head';
import { LogoOverlay, LogoOverlayWrapper } from 'components/logo-overlay';
import { StarRating } from 'components/star-rating';
import { Anchor } from 'constants/anchors';
import { Routes } from 'constants/routes';
import { IStreamer } from 'constants/types';
import Link from 'next/link';
import { useMemo } from 'react';
import { useStreamerReviewQuery } from 'hooks/api/streamer_reviews';
import { getItemIdentifier } from 'utils/slug';
import styles from './styles.module.css';

interface IStreamerCard {
  item: IStreamer;
  tags?: string[];
  isLink: boolean;
  variant: 'main' | 'user';
  priority?: boolean;
}

interface IUserRating {
  username: string;
  rating: number;
  variant: 'main' | 'user';
}

const UserRating = ({ username, rating, variant }: IUserRating) => {
  const starSize = variant === 'user' ? '20' : '14';
  const starGap = variant === 'user' ? '5.85px' : '4px';

  return (
    <div className={styles.container}>
      <h2
        className={clsx(
          styles.username,
          variant === 'user' && styles.username_user
        )}
      >
        {username}
      </h2>
      <div className={styles.ratingContainer}>
        <span
          className={clsx(
            styles.ratingValue,
            variant === 'user' && styles.ratingValue_user
          )}
        >
          {rating}
        </span>
        <StarRating
          ratingNumber={rating.toString()}
          color="white"
          starSize={starSize}
          starGap={starGap}
        />
      </div>
    </div>
  );
};

export const StreamerCard = ({
  item,
  variant,
  isLink,
  priority = false,
}: IStreamerCard) => {
  const { data: reviewsResponse } = useStreamerReviewQuery(
    { id: item._id },
    {
      skip: variant === 'user' || !item._id || item.reviews_count !== undefined,
    }
  );
  const reviewsCount = Number(
    item.reviews_count ?? reviewsResponse?.count ?? 0
  );

  const imgWidth = useMemo(() => {
    if (variant === 'user') {
      return 261;
    }

    return 180;
  }, [variant]);

  const imgHeight = useMemo(() => {
    if (variant === 'user') {
      return 395;
    }

    return 270;
  }, [variant]);

  const cardContent = (
    <div
      className={clsx(styles.card, {
        [styles.card_main]: variant === 'main',
        [styles.card_user]: variant === 'user',
      })}
    >
      <div className={styles.gradient} />

      <LogoOverlayWrapper>
        <Image
          src={item?.imageUrl ?? ''}
          width={imgWidth}
          height={imgHeight}
          alt={item?.username ? `${item.username} streamer` : 'Streamer'}
          priority={priority}
          fetchPriority={priority ? 'high' : undefined}
          loading={priority ? undefined : 'lazy'}
          sizes={
            variant === 'user' ? '(max-width: 768px) 100vw, 261px' : '180px'
          }
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />

        <LogoOverlay
          width={imgWidth}
          height={imgHeight}
          alt={item?.username}
          image={item?.logoUrl}
        />
      </LogoOverlayWrapper>

      <div
        className={clsx(
          styles.user_image_container,
          variant === 'user' && styles.image_container_user
        )}
      >
        <UserRating
          username={item.username}
          rating={item.rating}
          variant={variant}
        />
      </div>
    </div>
  );

  return isLink ? (
    <div className={styles.casino_card__wrapper}>
      {priority && item?.imageUrl ? (
        <Head>
          <link
            rel="preload"
            as="image"
            href={item.imageUrl}
            fetchPriority="high"
          />
        </Head>
      ) : null}
      <Link href={Routes.streamer(getItemIdentifier(item))}>{cardContent}</Link>
      <Link
        href={
          Routes.streamer(getItemIdentifier(item)) +
          `#${Anchor.StreamerReviews}`
        }
        className={styles.review__link}
      >
        {'Read reviews'} ({reviewsCount})
      </Link>
    </div>
  ) : (
    <div className={styles.card}>
      {priority && item?.imageUrl ? (
        <Head>
          <link
            rel="preload"
            as="image"
            href={item.imageUrl}
            fetchPriority="high"
          />
        </Head>
      ) : null}
      {cardContent}
    </div>
  );
};
